import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, getProductionId } from "@/lib/api";
import { toast } from "sonner";
import {
  Input,
} from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Save, Trash2, GripVertical, Type, AlignLeft, CheckSquare,
  Circle, Calendar, PenLine, BookOpen, ArrowLeft, X, LayoutTemplate,
  BookmarkPlus, ChevronDown, Eye, Edit3,
} from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import FormFill from "@/pages/FormFill";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const FIELD_META = {
  text:      { label: "Short text",   icon: Type },
  textarea:  { label: "Long text",    icon: AlignLeft },
  checkbox:  { label: "Acknowledge",  icon: CheckSquare },
  radio:     { label: "Multi-choice", icon: Circle },
  date:      { label: "Date",         icon: Calendar },
  signature: { label: "Signature",    icon: PenLine },
  content:   { label: "Policy text",  icon: BookOpen },
};

const ROLE_OPTS = [
  { v: "org_admin", label: "Org Admin" },
  { v: "producer", label: "Producer" },
  { v: "production_staff", label: "Production Staff" },
  { v: "support_staff", label: "Support Staff" },
  { v: "cast", label: "Cast" },
];

function newField(type) {
  const base = { id: Math.random().toString(36).slice(2), type, label: FIELD_META[type].label, required: false };
  if (type === "radio") base.options = ["Option 1", "Option 2"];
  if (type === "content") base.content = "<p>Paste the policy or agreement text here…</p>";
  return base;
}

export default function FormBuilder() {
  const params = useParams();
  const location = useLocation();
  const nav = useNavigate();
  // `/forms/new` doesn't have a `:id` route param, so useParams gives us
  // undefined. Fall back to the pathname so we correctly detect the "new
  // form" case — otherwise we'd try to GET /forms/undefined and 404.
  const isNew = !params.id || location.pathname === "/forms/new";
  const id = isNew ? null : params.id;
  const productionId = getProductionId();

  const [form, setForm] = useState({
    title: "", description: "", fields: [],
    is_required: false, active: true,
    signature_mode: "either",
    assignment: { scope: "all", roles: [], user_ids: [] },
  });
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const params = { production_id: productionId };
      const [mem, tpl] = await Promise.all([
        api.get("/users", { params }).catch(() => ({ data: [] })),
        api.get("/form-templates", { params }).catch(() => ({ data: [] })),
      ]);
      setMembers(mem.data || []);
      setTemplates(tpl.data || []);
      if (!isNew) {
        try {
          const { data } = await api.get(`/forms/${id}`);
          setForm({
            title: data.title || "",
            description: data.description || "",
            fields: data.fields || [],
            is_required: !!data.is_required,
            active: data.active !== false,
            signature_mode: data.signature_mode || "either",
            assignment: data.assignment || { scope: "all", roles: [], user_ids: [] },
          });
        } catch (e) {
          // Impersonation switches tenants and can leave stale form URLs
          // behind. Silently redirect rather than scaring the user.
          nav("/forms", { replace: true });
        } finally { setLoading(false); }
      }
    })();
  }, [id]);

  const addField = (type) => setForm((f) => ({ ...f, fields: [...f.fields, newField(type)] }));
  const patchField = (idx, patch) => setForm((f) => ({
    ...f, fields: f.fields.map((x, i) => i === idx ? { ...x, ...patch } : x),
  }));
  const removeField = (idx) => setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));
  const moveField = (idx, dir) => setForm((f) => {
    const next = [...f.fields];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return f;
    [next[idx], next[j]] = [next[j], next[idx]];
    return { ...f, fields: next };
  });

  const patchAssignment = (patch) => setForm((f) => ({ ...f, assignment: { ...f.assignment, ...patch } }));

  const canSave = form.title.trim().length > 0;
  const save = async () => {
    if (!canSave) return toast.error("Title is required");
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/forms", form, { params: { production_id: productionId } });
        toast.success("Form created");
        nav(`/forms/${data.id}/edit`, { replace: true });
      } else {
        await api.patch(`/forms/${id}`, form);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveAsTemplate = async () => {
    if (!canSave) return toast.error("Title is required");
    try {
      await api.post("/form-templates", {
        title: form.title,
        description: form.description,
        fields: form.fields,
        signature_mode: form.signature_mode,
      }, { params: { production_id: productionId } });
      toast.success(`Saved "${form.title}" as a template`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save template");
    }
  };

  const startFromTemplate = (t) => {
    setForm((f) => ({
      ...f,
      title: t.title || f.title,
      description: t.description || f.description,
      fields: (t.fields || []).map((x) => ({ ...x, id: Math.random().toString(36).slice(2) })),
      signature_mode: t.signature_mode || "either",
    }));
    toast.success(`Loaded "${t.title}" — save when you're ready`);
  };

  const memberOptions = useMemo(() => (members || []).filter(m => m.role !== "super_admin"), [members]);

  if (loading) return <div className="text-zinc-500 text-sm p-8">Loading…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="form-builder">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => nav("/forms")}
            className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1"
            data-testid="back-to-forms"
          ><ArrowLeft className="w-3 h-3" /> Back to Forms</button>
          <h1 className="mt-1 font-display text-3xl tracking-tight font-light">
            {isNew ? "New form" : "Edit form"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="preview-form-btn"
            disabled={!form.title.trim() && form.fields.length === 0}
            onClick={() => setPreviewOpen(true)}
            className="h-10 px-4 rounded border border-white/10 hover:bg-white/5 text-sm inline-flex items-center gap-1.5 disabled:opacity-40 disabled:hover:bg-transparent"
          ><Eye className="w-4 h-4" /> Preview</button>
          <button
            data-testid="save-form-btn"
            disabled={!canSave || saving}
            onClick={save}
            style={goldBtn}
            className="h-10 px-4 rounded font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5 disabled:opacity-50"
          ><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save form"}</button>
        </div>
      </div>

      {/* Template quick-actions */}
      <div className="flex flex-wrap items-center gap-2">
        {isNew && templates.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="start-from-template-btn"
                className="text-xs h-9 px-3 rounded border border-white/10 hover:bg-white/5 inline-flex items-center gap-1.5"
              >
                <LayoutTemplate className="w-3.5 h-3.5" /> Start from template
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#121212] border-white/10 text-white w-72" align="start">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">
                Your templates
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              {templates.map((t) => (
                <DropdownMenuItem
                  key={t.id}
                  data-testid={`start-tpl-${t.id}`}
                  onClick={() => startFromTemplate(t)}
                  className="focus:bg-white/5 cursor-pointer"
                >
                  <div className="min-w-0">
                    <div className="text-sm truncate">{t.title}</div>
                    <div className="text-[11px] text-zinc-500">
                      {(t.fields || []).length} field{(t.fields || []).length === 1 ? "" : "s"}
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <button
          data-testid="save-as-template-btn"
          disabled={!canSave || saving}
          onClick={saveAsTemplate}
          className="text-xs h-9 px-3 rounded border border-white/10 hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50"
        ><BookmarkPlus className="w-3.5 h-3.5" /> Save as template</button>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Left — form definition */}
        <div className="space-y-6">
          <section className="rounded-lg border border-white/10 bg-[#121212] p-5 space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                data-testid="form-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Cast Media Release 2026"
                className="bg-[#0A0A0A] border-white/10"
              />
            </div>
            <div>
              <Label>Description <span className="text-zinc-600 text-xs">(shown at the top of the form)</span></Label>
              <Textarea
                data-testid="form-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="A short blurb about what signers are agreeing to."
                className="bg-[#0A0A0A] border-white/10"
              />
            </div>
          </section>

          <section className="rounded-lg border border-white/10 bg-[#121212] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg">Fields</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(FIELD_META).map(([k, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <button
                      key={k}
                      data-testid={`add-field-${k}`}
                      onClick={() => addField(k)}
                      className="text-[11px] inline-flex items-center gap-1 h-8 px-2.5 rounded border border-white/10 hover:border-gold/40 hover:bg-white/5"
                    ><Icon className="w-3 h-3" /> {meta.label}</button>
                  );
                })}
              </div>
            </div>

            {form.fields.length === 0 ? (
              <div className="text-xs text-zinc-500 py-8 text-center border border-dashed border-white/10 rounded">
                Add fields using the chips above. Drag to reorder after.
              </div>
            ) : (
              <div className="space-y-3">
                {form.fields.map((f, idx) => {
                  const Icon = FIELD_META[f.type].icon;
                  return (
                    <div key={f.id || idx} className="rounded border border-white/10 bg-[#0A0A0A] p-3" data-testid={`field-${idx}`}>
                      <div className="flex items-start gap-2">
                        <div className="pt-1 text-zinc-500 flex flex-col gap-1">
                          <button data-testid={`field-up-${idx}`} onClick={() => moveField(idx, -1)} className="hover:text-white" title="Move up">▲</button>
                          <GripVertical className="w-3.5 h-3.5" />
                          <button data-testid={`field-down-${idx}`} onClick={() => moveField(idx, 1)} className="hover:text-white" title="Move down">▼</button>
                        </div>
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">
                              <Icon className="w-3 h-3" /> {FIELD_META[f.type].label}
                            </span>
                            {f.type !== "content" && f.type !== "signature" && (
                              <label className="ml-auto text-[11px] text-zinc-400 inline-flex items-center gap-2">
                                <Switch
                                  data-testid={`field-required-${idx}`}
                                  checked={!!f.required}
                                  onCheckedChange={(v) => patchField(idx, { required: v })}
                                />
                                Required
                              </label>
                            )}
                          </div>
                          {f.type === "content" ? (
                            <RichTextEditor
                              testid={`field-content-${idx}`}
                              value={f.content || ""}
                              onChange={(html) => patchField(idx, { content: html })}
                              placeholder="The policy or agreement text signers will read. Use the toolbar for headings, lists, and links."
                              minHeight={140}
                            />
                          ) : (
                            <>
                              <Input
                                data-testid={`field-label-${idx}`}
                                value={f.label}
                                onChange={(e) => patchField(idx, { label: e.target.value })}
                                placeholder="Field label (e.g. 'I have read and agree')"
                                className="bg-[#121212] border-white/10"
                              />
                              {f.type === "radio" && (
                                <div className="space-y-1.5">
                                  {(f.options || []).map((opt, oi) => (
                                    <div key={oi} className="flex items-center gap-2">
                                      <Input
                                        data-testid={`field-${idx}-option-${oi}`}
                                        value={opt}
                                        onChange={(e) => {
                                          const opts = [...(f.options || [])];
                                          opts[oi] = e.target.value;
                                          patchField(idx, { options: opts });
                                        }}
                                        className="bg-[#121212] border-white/10 text-sm"
                                      />
                                      <button
                                        onClick={() => patchField(idx, { options: (f.options || []).filter((_, i) => i !== oi) })}
                                        className="text-zinc-500 hover:text-red-400 p-1"
                                        title="Remove option"
                                      ><X className="w-3 h-3" /></button>
                                    </div>
                                  ))}
                                  <button
                                    data-testid={`field-${idx}-add-option`}
                                    onClick={() => patchField(idx, { options: [...(f.options || []), `Option ${(f.options?.length || 0) + 1}`] })}
                                    className="text-[11px] text-gold hover:brightness-125 inline-flex items-center gap-1"
                                  ><Plus className="w-3 h-3" /> Add option</button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <button
                          data-testid={`field-remove-${idx}`}
                          onClick={() => removeField(idx)}
                          className="text-zinc-500 hover:text-red-400 p-1"
                          title="Remove field"
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Right — settings */}
        <aside className="space-y-4">
          <section className="rounded-lg border border-white/10 bg-[#121212] p-5 space-y-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Settings</div>

            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm">Required at sign-in</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Hard-blocks the app until signed.</div>
              </div>
              <Switch
                data-testid="form-required-toggle"
                checked={form.is_required}
                onCheckedChange={(v) => setForm({ ...form, is_required: v })}
              />
            </div>

            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm">Active</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Off = hidden from signers.</div>
              </div>
              <Switch
                data-testid="form-active-toggle"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Signature mode</Label>
              <Select value={form.signature_mode} onValueChange={(v) => setForm({ ...form, signature_mode: v })}>
                <SelectTrigger data-testid="form-sig-mode" className="mt-1 bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  <SelectItem value="either">Either draw or type</SelectItem>
                  <SelectItem value="draw">Draw only</SelectItem>
                  <SelectItem value="type">Type only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          <section className="rounded-lg border border-white/10 bg-[#121212] p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Who signs this?</div>
            <Select value={form.assignment.scope} onValueChange={(v) => patchAssignment({ scope: v })}>
              <SelectTrigger data-testid="form-scope-select" className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                <SelectItem value="all">Everyone in the production</SelectItem>
                <SelectItem value="roles">Specific roles</SelectItem>
                <SelectItem value="users">Specific people</SelectItem>
              </SelectContent>
            </Select>

            {form.assignment.scope === "roles" && (
              <div className="space-y-1.5" data-testid="form-role-picker">
                {ROLE_OPTS.map((r) => {
                  const checked = form.assignment.roles?.includes(r.v);
                  return (
                    <label key={r.v} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`form-role-${r.v}`}
                        checked={!!checked}
                        onChange={(e) => {
                          const cur = new Set(form.assignment.roles || []);
                          if (e.target.checked) cur.add(r.v); else cur.delete(r.v);
                          patchAssignment({ roles: Array.from(cur) });
                        }}
                      /> {r.label}
                    </label>
                  );
                })}
              </div>
            )}

            {form.assignment.scope === "users" && (
              <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="form-user-picker">
                {memberOptions.map((u) => {
                  const checked = form.assignment.user_ids?.includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`form-user-${u.id}`}
                        checked={!!checked}
                        onChange={(e) => {
                          const cur = new Set(form.assignment.user_ids || []);
                          if (e.target.checked) cur.add(u.id); else cur.delete(u.id);
                          patchAssignment({ user_ids: Array.from(cur) });
                        }}
                      />
                      <span className="truncate">{u.name}</span>
                      <span className="text-xs text-zinc-500 truncate">{u.email}</span>
                    </label>
                  );
                })}
                {memberOptions.length === 0 && <div className="text-xs text-zinc-500">No members yet.</div>}
              </div>
            )}
          </section>
        </aside>
      </div>

      {previewOpen && (
        <div
          data-testid="preview-overlay"
          className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="w-full max-w-3xl bg-[#0A0A0A] border border-white/10 rounded-lg shadow-2xl">
            <header className="border-b border-white/10 p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold inline-flex items-center gap-1.5">
                  <Eye className="w-3 h-3" /> Live preview
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Exactly what a signer will see. Submit is disabled here.
                </div>
              </div>
              <button
                data-testid="preview-close-btn"
                onClick={() => setPreviewOpen(false)}
                className="text-xs inline-flex items-center gap-1.5 h-9 px-3 rounded border border-white/10 hover:bg-white/5"
              ><Edit3 className="w-3.5 h-3.5" /> Back to editing</button>
            </header>
            <div className="p-5 md:p-6 max-h-[75vh] overflow-y-auto">
              <div className="mb-4">
                <h2 className="font-display text-2xl tracking-tight">{form.title || "Untitled form"}</h2>
              </div>
              <FormFill
                previewMode
                embedded
                previewForm={{
                  id: id || "preview",
                  production_id: productionId,
                  title: form.title,
                  description: form.description,
                  fields: form.fields,
                  signature_mode: form.signature_mode,
                  is_required: form.is_required,
                  active: form.active,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
